// ── myPTOnline SUPERUSER ADMIN PANEL ─────────────────────────────────────────
// Self-contained React component. Has its own login, loads data from Supabase.
// Mount at route "admin" in mypt-app.jsx.

import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "./supabase.js";
import { resetDB, getAll } from "./db.js";

// ── THEME (mirrors mypt-app.jsx C object) ─────────────────────────────────────
const C = {
  bg:           "#0c0c0e",
  surface:      "#141417",
  surfaceAlt:   "#1c1c20",
  border:       "rgba(255,255,255,0.10)",
  borderStrong: "rgba(255,255,255,0.22)",
  text:         "#f0f0f0",
  textSub:      "#b8b8c0",
  textMuted:    "#8e8e98",
  primary:      "#c8ff57",
  primaryDark:  "#a8d940",
  primaryGlow:  "rgba(200,255,87,0.10)",
  danger:       "#ff4d4d",
  dangerLight:  "rgba(255,77,77,0.10)",
  blue:         "#5b9fff",
  blueLight:    "rgba(91,159,255,0.10)",
  amber:        "#ffb347",
  amberLight:   "rgba(255,179,71,0.10)",
  green:        "#30d158",
  greenLight:   "rgba(48,209,88,0.10)",
};

// ── SHARED MINI-COMPONENTS ────────────────────────────────────────────────────
function Btn({ onClick, children, color = C.primary, textColor = "#000", sm = false, outline = false, danger = false }) {
  const bg = danger ? C.danger : outline ? "transparent" : color;
  const tc = danger ? "#fff" : outline ? C.text : textColor;
  const border = outline ? `1px solid ${C.borderStrong}` : "none";
  return (
    <button onClick={onClick} style={{
      background: bg, color: tc, border,
      padding: sm ? "5px 12px" : "9px 18px",
      borderRadius: 7, fontSize: sm ? 12 : 13, fontWeight: 600,
      cursor: "pointer", transition: "opacity 0.15s", whiteSpace: "nowrap",
    }}
      onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
      onMouseLeave={e => e.currentTarget.style.opacity = "1"}
    >{children}</button>
  );
}

function Badge({ label, color, bg }) {
  return (
    <span style={{
      display: "inline-block", padding: "2px 10px", borderRadius: 20,
      fontSize: 11, fontWeight: 600, letterSpacing: 0.3,
      background: bg, color,
    }}>{label}</span>
  );
}

function StatCard({ num, label, accent = C.primary }) {
  return (
    <div style={{
      background: C.surfaceAlt, border: `1px solid ${C.border}`,
      borderRadius: 12, padding: "18px 16px", textAlign: "center",
    }}>
      <div style={{ fontSize: 30, fontWeight: 700, color: accent, lineHeight: 1 }}>{num}</div>
      <div style={{ fontSize: 11, color: C.textMuted, marginTop: 6, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase" }}>{label}</div>
    </div>
  );
}

function SectionCard({ children, title }) {
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, marginBottom: 20 }}>
      {title && <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, color: C.text }}>{title}</h3>}
      {children}
    </div>
  );
}

function Table({ cols, rows }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {cols.map(c => (
              <th key={c} style={{
                padding: "10px 14px", textAlign: "left", fontSize: 11,
                fontWeight: 600, letterSpacing: 0.6, textTransform: "uppercase",
                color: C.textMuted, borderBottom: `1px solid ${C.border}`,
              }}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} onMouseEnter={e => { e.currentTarget.style.background = C.surfaceAlt; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
              {row.map((cell, j) => (
                <td key={j} style={{
                  padding: "13px 14px", fontSize: 13,
                  borderBottom: i < rows.length - 1 ? `1px solid ${C.border}` : "none",
                  color: C.text,
                }}>{cell}</td>
              ))}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={cols.length} style={{ padding: "24px 14px", textAlign: "center", color: C.textMuted, fontSize: 13 }}>No records found.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function Input({ placeholder, value, onChange, type = "text", style = {} }) {
  return (
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        padding: "9px 13px", background: C.surfaceAlt, color: C.text,
        border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13,
        outline: "none", fontFamily: "inherit", ...style,
      }}
      onFocus={e => e.target.style.borderColor = C.primary}
      onBlur={e => e.target.style.borderColor = C.border}
    />
  );
}

function Select({ value, onChange, children, style = {} }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} style={{
      padding: "9px 13px", background: C.surfaceAlt, color: C.text,
      border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13,
      outline: "none", fontFamily: "inherit", ...style,
    }}>{children}</select>
  );
}

// ── SUBSCRIPTION BADGE HELPER ─────────────────────────────────────────────────
function subBadge(status) {
  const map = {
    trial:   { label: "Trial",    color: C.amber,   bg: C.amberLight },
    active:  { label: "Active",   color: C.green,   bg: C.greenLight },
    expired: { label: "Expired",  color: C.danger,  bg: C.dangerLight },
    free:    { label: "Free",     color: C.blue,    bg: C.blueLight },
    default: { label: status||"—", color: C.textMuted, bg: C.surfaceAlt },
  };
  const s = map[status] || map.default;
  return <Badge label={s.label} color={s.color} bg={s.bg} />;
}

// ── MODAL ─────────────────────────────────────────────────────────────────────
function Modal({ title, onClose, children }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)",
      backdropFilter: "blur(4px)", display: "flex", alignItems: "center",
      justifyContent: "center", zIndex: 500, padding: 24,
    }}>
      <div style={{
        background: C.surface, border: `1px solid ${C.borderStrong}`,
        borderRadius: 18, width: "100%", maxWidth: 520,
        padding: 28, maxHeight: "88vh", overflowY: "auto",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <span style={{ fontWeight: 700, fontSize: 17 }}>{title}</span>
          <button onClick={onClose} style={{
            background: C.surfaceAlt, border: `1px solid ${C.border}`,
            color: C.textSub, width: 32, height: 32, borderRadius: 8,
            fontSize: 18, display: "flex", alignItems: "center",
            justifyContent: "center", cursor: "pointer",
          }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── ADMIN LOGIN ───────────────────────────────────────────────────────────────
function AdminLogin({ onLogin }) {
  const [passphrase, setPassphrase] = useState("");
  const [error, setError]           = useState("");
  const [loading, setLoading]       = useState(false);

  const handleLogin = async () => {
    setError(""); setLoading(true);
    try {
      const res = await fetch("/api/admin-login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ passphrase: passphrase.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.token) {
        localStorage.setItem("mypt_admin_token", data.token);
        onLogin(data.token);
      } else {
        setError(data.error || "Invalid passphrase.");
      }
    } catch {
      setError("Couldn't reach the server. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center",
      justifyContent: "center", background: C.bg, fontFamily: "'Space Grotesk', system-ui, sans-serif",
    }}>
      <div style={{
        background: C.surface, border: `1px solid ${C.borderStrong}`,
        borderRadius: 18, padding: "40px 36px", width: "100%", maxWidth: 380,
      }}>
        <div style={{ marginBottom: 28, textAlign: "center" }}>
          <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.3px" }}>
            my<span style={{ color: C.primary }}>PT</span>
            <span style={{ color: C.textMuted, fontWeight: 400 }}>Online</span>
          </div>
          <div style={{ fontSize: 13, color: C.textMuted, marginTop: 6 }}>Superuser Admin Panel</div>
        </div>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.8, textTransform: "uppercase", color: C.textMuted, marginBottom: 6 }}>Passphrase</div>
          <Input placeholder="Enter passphrase" type="password" value={passphrase} onChange={setPassphrase} style={{ width: "100%" }} />
        </div>
        {error && <div style={{ color: C.danger, fontSize: 13, marginBottom: 14 }}>{error}</div>}
        <button
          onClick={handleLogin}
          disabled={loading}
          onKeyDown={e => e.key === "Enter" && handleLogin()}
          style={{
            width: "100%", padding: "11px", background: C.primary, color: "#000",
            border: "none", borderRadius: 9, fontWeight: 700, fontSize: 14,
            cursor: loading ? "default" : "pointer", opacity: loading ? 0.7 : 1,
            fontFamily: "inherit",
          }}
        >{loading ? "Signing in…" : "Sign In"}</button>
        <p style={{ fontSize: 11, color: C.textMuted, textAlign: "center", marginTop: 16 }}>
          This panel is for platform administrators only.
        </p>
      </div>
    </div>
  );
}

// ── TAB 1 — OVERVIEW ─────────────────────────────────────────────────────────
function OverviewTab({ stats, pts }) {
  const recent = [...pts].sort((a, b) => b.id - a.id).slice(0, 5);
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 14, marginBottom: 24 }}>
        <StatCard num={stats.totalPTs}       label="Total PTs"          accent={C.primary} />
        <StatCard num={stats.totalClients}   label="Total Clients"      accent={C.blue} />
        <StatCard num={stats.activeSubs}     label="Active Subs"        accent={C.green} />
        <StatCard num={stats.trialUsers}     label="Trial Users"        accent={C.amber} />
        <StatCard num={stats.expiredTrials}  label="Expired Trials"     accent={C.danger} />
        <StatCard num={stats.totalSessions}  label="Total Sessions"     accent={C.textSub} />
      </div>
      <SectionCard title="Recent PT Sign-ups">
        <Table
          cols={["ID", "Name", "Email", "Location", "Specialism", "Level", "Joined"]}
          rows={recent.map(p => [
            `#${p.id}`,
            p.name,
            p.email || "—",
            p.location || "—",
            p.specialism || "—",
            p.level ? `Level ${p.level}` : "—",
            p.created_at ? new Date(p.created_at).toLocaleDateString("en-GB") : "—",
          ])}
        />
      </SectionCard>
    </div>
  );
}

// ── TAB 2 — PT MANAGEMENT ────────────────────────────────────────────────────
function PTManagementTab({ pts, clients, subscriptions, onRefresh, adminToken, onAuthExpired }) {
  const [search, setSearch] = useState("");
  const [requireApproval, setRequireApproval] = useState(true);
  const [approvalLoading, setApprovalLoading] = useState(false);

  // Admin writes to `pts` must go through the server (service role) — the browser
  // runs as anon and `pts` RLS is read-only, so a client-side update silently
  // affects 0 rows. Returns the JSON body on success, or null (after alerting).
  const adminAction = async (action, ptId) => {
    try {
      const res = await fetch("/api/admin-action", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: adminToken, action, ptId }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) {
        alert("Your admin session has expired — please sign in again.");
        onAuthExpired && onAuthExpired();
        return null;
      }
      if (!res.ok || !data.ok) { alert("Error: " + (data.error || res.statusText)); return null; }
      return data;
    } catch (e) {
      alert("Network error: " + e.message);
      return null;
    }
  };

  useEffect(() => {
    supabase.from("app_config").select("value").eq("key", "require_pt_approval").single()
      .then(({ data }) => { if (data) setRequireApproval(data.value === "true"); });
  }, []);

  const toggleApproval = async () => {
    const next = !requireApproval;
    setApprovalLoading(true);
    const { error } = await supabase.from("app_config")
      .upsert({ key: "require_pt_approval", value: String(next), updated_at: new Date().toISOString() }, { onConflict: "key" });
    setApprovalLoading(false);
    if (error) { alert("Error saving setting: " + error.message); return; }
    setRequireApproval(next);
  };

  const getClientCount = ptId => clients.filter(c => c.pt_id === ptId || c.ptId === ptId).length;
  const getSub = ptId => subscriptions.find(s => s.pt_id === ptId || s.ptId === ptId);

  const trialDaysLeft = pt => {
    try {
      const stored = JSON.parse(localStorage.getItem("mypt_trial_start") || "{}");
      const start = stored[pt.id] || pt.trial_start || pt.created_at;
      if (!start) return 60;
      const elapsed = Math.floor((Date.now() - new Date(start)) / 86400000);
      return Math.max(0, 60 - elapsed);
    } catch { return 60; }
  };

  const extendTrial = async ptId => {
    const data = await adminAction("extend_trial", ptId);
    if (!data) return;
    // Mirror the persisted trial_start into localStorage so the countdown (which
    // reads localStorage first) updates immediately.
    try {
      const stored = JSON.parse(localStorage.getItem("mypt_trial_start") || "{}");
      if (data.trial_start) stored[ptId] = data.trial_start;
      localStorage.setItem("mypt_trial_start", JSON.stringify(stored));
    } catch {}
    alert(`Trial extended by 30 days for PT #${ptId}`);
    onRefresh();
  };

  const activate = async ptId => {
    const data = await adminAction("activate", ptId);
    if (!data) return;
    alert(`PT #${ptId} activated.`);
    onRefresh();
  };

  const grantFree = async ptId => {
    const data = await adminAction("grant_free", ptId);
    if (!data) return;
    alert(`Free access granted to PT #${ptId}`);
    onRefresh();
  };

  const clearFree = async ptId => {
    const data = await adminAction("clear_free", ptId);
    if (!data) return;
    onRefresh();
  };

  const suspend = async ptId => {
    if (!window.confirm(`Suspend PT #${ptId}? They will be marked suspended in the panel.`)) return;
    const data = await adminAction("suspend", ptId);
    if (!data) return;
    alert(`PT #${ptId} suspended.`);
    onRefresh();
  };

  const filtered = pts.filter(p => {
    const q = search.toLowerCase();
    return !q || p.name?.toLowerCase().includes(q) || p.location?.toLowerCase().includes(q) || p.specialism?.toLowerCase().includes(q);
  });

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
        <Input placeholder="Search by name, location or specialism…" value={search} onChange={setSearch} style={{ width: "100%", maxWidth: 360 }} />
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 14px", background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 10 }}>
          <span style={{ fontSize: 13, color: C.textSub }}>Require approval for new PTs</span>
          <button
            onClick={toggleApproval}
            disabled={approvalLoading}
            style={{
              width: 44, height: 24, borderRadius: 12, border: "none", cursor: approvalLoading ? "default" : "pointer",
              background: requireApproval ? C.primary : C.surfaceAlt,
              border: `1px solid ${requireApproval ? C.primary : C.borderStrong}`,
              position: "relative", transition: "background 0.2s", flexShrink: 0,
              opacity: approvalLoading ? 0.6 : 1,
            }}
            title={requireApproval ? "Approval required — click to disable" : "Approval not required — click to enable"}
          >
            <span style={{
              position: "absolute", top: 3, left: requireApproval ? 22 : 3,
              width: 16, height: 16, borderRadius: "50%",
              background: requireApproval ? "#0c0c0e" : C.textMuted,
              transition: "left 0.2s",
            }} />
          </button>
        </div>
      </div>
      <SectionCard>
        <Table
          cols={["PT", "Email", "Location", "Specialism", "Lvl", "Clients", "Subscription", "Trial Days", "Actions"]}
          rows={filtered.map(p => {
            // Plan/subscription state lives on pts (the anon admin client can't read
            // the RLS-locked subscriptions table). Fall back to any real sub, then trial.
            const subStatus = p.plan || getSub(p.id)?.status || "trial";
            const days = trialDaysLeft(p);
            return [
              <span>
                <strong style={{ color: C.text }}>{p.name}</strong>
                {p.status === "pending" && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4, background: C.amber + "22", color: C.amber, textTransform: "uppercase", letterSpacing: 0.5 }}>Pending</span>}
                {p.status === "suspended" && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4, background: C.danger + "22", color: C.danger, textTransform: "uppercase", letterSpacing: 0.5 }}>Suspended</span>}
                <br /><span style={{ fontSize: 11, color: C.textMuted }}>#{p.id}</span>
              </span>,
              <span style={{ fontSize: 12, color: C.textSub }}>{p.email || "—"}</span>,
              p.location || "—",
              p.specialism || "—",
              p.level || "—",
              getClientCount(p.id),
              subBadge(subStatus),
              subStatus === "trial" ? <span style={{ color: days === 0 ? C.danger : days <= 5 ? C.amber : C.textSub }}>{days}d</span> : "—",
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {(p.status === "pending" || p.status === "suspended") &&
                  <Btn sm onClick={() => activate(p.id)} color="#22c55e" textColor="#fff">{p.status === "suspended" ? "Reactivate" : "Activate"}</Btn>}
                <Btn sm onClick={() => extendTrial(p.id)}>+30d</Btn>
                {p.plan === "free"
                  ? <Btn sm onClick={() => clearFree(p.id)}>Un-free</Btn>
                  : <Btn sm onClick={() => grantFree(p.id)} color={C.blue} textColor="#fff">Free</Btn>}
                {p.status !== "suspended" && <Btn sm danger onClick={() => suspend(p.id)}>Suspend</Btn>}
              </div>,
            ];
          })}
        />
      </SectionCard>
    </div>
  );
}

// ── TAB 3 — CLIENT MANAGEMENT ────────────────────────────────────────────────
function ClientManagementTab({ clients, pts }) {
  const [filterPT, setFilterPT] = useState("all");

  const getPTName = ptId => {
    const pt = pts.find(p => p.id === (ptId));
    return pt?.name || `PT #${ptId}`;
  };

  const filtered = filterPT === "all" ? clients : clients.filter(c => String(c.pt_id || c.ptId) === filterPT);

  const statusBadge = status => {
    const map = {
      "active":          { color: C.green,  bg: C.greenLight,  label: "Active" },
      "awaiting-notes":  { color: C.amber,  bg: C.amberLight,  label: "Awaiting Notes" },
      "awaiting-signup": { color: C.blue,   bg: C.blueLight,   label: "Awaiting Signup" },
      "inactive":        { color: C.danger, bg: C.dangerLight, label: "Inactive" },
    };
    const s = map[status] || { color: C.textMuted, bg: C.surfaceAlt, label: status || "—" };
    return <Badge label={s.label} color={s.color} bg={s.bg} />;
  };

  return (
    <div>
      <div style={{ marginBottom: 16, display: "flex", gap: 12, alignItems: "center" }}>
        <span style={{ fontSize: 13, color: C.textSub }}>Filter by PT:</span>
        <Select value={filterPT} onChange={setFilterPT} style={{ minWidth: 200 }}>
          <option value="all">All PTs</option>
          {pts.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
        </Select>
      </div>
      <SectionCard>
        <Table
          cols={["Client", "PT", "Email", "Mobile", "Status", "Joined"]}
          rows={filtered.map(c => [
            c.name || "—",
            getPTName(c.pt_id || c.ptId),
            c.email || "—",
            c.mobile || "—",
            statusBadge(c.status),
            c.created_at ? new Date(c.created_at).toLocaleDateString("en-GB") : "—",
          ])}
        />
      </SectionCard>
    </div>
  );
}

// ── TAB 4 — SUBSCRIPTIONS ────────────────────────────────────────────────────
function SubscriptionsTab({ subscriptions, pts, onRefresh }) {
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ ptId: "", plan: "monthly", status: "active" });
  const [saving, setSaving] = useState(false);

  const getPTName = ptId => pts.find(p => p.id === Number(ptId))?.name || `PT #${ptId}`;

  const handleAdd = async () => {
    if (!form.ptId) { alert("Select a PT."); return; }
    setSaving(true);
    const { error } = await supabase.from("subscriptions").insert({
      pt_id: Number(form.ptId),
      plan: form.plan,
      status: form.status,
      started_at: new Date().toISOString(),
    });
    setSaving(false);
    if (error) { alert("Error: " + error.message); return; }
    setShowModal(false);
    onRefresh();
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
        <Btn onClick={() => setShowModal(true)}>+ Add Manual Subscription</Btn>
      </div>
      <SectionCard>
        <Table
          cols={["PT", "Plan", "Status", "Started", "Expires", "Stripe Customer"]}
          rows={subscriptions.map(s => [
            getPTName(s.pt_id || s.ptId),
            s.plan || "—",
            subBadge(s.status),
            s.started_at ? new Date(s.started_at).toLocaleDateString("en-GB") : "—",
            s.expires_at ? new Date(s.expires_at).toLocaleDateString("en-GB") : "—",
            <span style={{ fontSize: 11, color: C.textMuted }}>{s.stripe_customer_id || "—"}</span>,
          ])}
        />
      </SectionCard>
      {showModal && (
        <Modal title="Add Manual Subscription" onClose={() => setShowModal(false)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.8, textTransform: "uppercase", color: C.textMuted, marginBottom: 6 }}>PT</div>
              <Select value={form.ptId} onChange={v => setForm(f => ({ ...f, ptId: v }))} style={{ width: "100%" }}>
                <option value="">— Select PT —</option>
                {pts.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Select>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.8, textTransform: "uppercase", color: C.textMuted, marginBottom: 6 }}>Plan</div>
              <Select value={form.plan} onChange={v => setForm(f => ({ ...f, plan: v }))} style={{ width: "100%" }}>
                <option value="monthly">Monthly</option>
                <option value="annual">Annual</option>
                <option value="free">Free</option>
              </Select>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.8, textTransform: "uppercase", color: C.textMuted, marginBottom: 6 }}>Status</div>
              <Select value={form.status} onChange={v => setForm(f => ({ ...f, status: v }))} style={{ width: "100%" }}>
                <option value="active">Active</option>
                <option value="trial">Trial</option>
                <option value="free">Free</option>
                <option value="expired">Expired</option>
              </Select>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
              <Btn outline onClick={() => setShowModal(false)}>Cancel</Btn>
              <Btn onClick={handleAdd} color={C.primary}>{saving ? "Saving…" : "Save Subscription"}</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── TAB 5 — EXERCISE LIBRARY ─────────────────────────────────────────────────
// POST to the token-gated admin endpoint; surfaces server errors and signs the
// admin out on an expired token.
async function adminRequest(adminToken, body, onAuthExpired) {
  const res = await fetch("/api/admin-action", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token: adminToken, ...body }),
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401) { onAuthExpired?.(); throw new Error(data.error || "Admin session expired."); }
  if (!res.ok || !data.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

// ── Open-source exercise import (free-exercise-db) ───────────────────────────
// Public-domain dataset (The Unlicense, github.com/yuhonas/free-exercise-db):
// ~870 exercises with muscles, equipment, level, step-by-step instructions and
// images. Fetched straight from GitHub in the browser (no key, CORS-enabled) and
// written to the platform library (pt_id NULL) via the admin endpoint, which
// dedupes by source id and name. Values are mapped onto the app's own taxonomy
// (the PT exercise form's body areas + categories) so filters stay consistent.
const FEDB_BASE = "https://raw.githubusercontent.com/yuhonas/free-exercise-db/main";
const FEDB_BODY_AREA = {
  chest: "Chest", shoulders: "Shoulder", neck: "Shoulder",
  biceps: "Arms", triceps: "Arms", forearms: "Arms",
  lats: "Back", "middle back": "Back", "lower back": "Back", traps: "Back",
  quadriceps: "Legs", hamstrings: "Legs", glutes: "Legs", calves: "Legs", adductors: "Legs", abductors: "Legs",
  abdominals: "Core",
};
const FEDB_CATEGORY = {
  strength: "Strength", powerlifting: "Strength", "olympic weightlifting": "Strength", strongman: "Strength",
  plyometrics: "Strength", stretching: "Mobility", cardio: "Cardio",
};
const titleCase = s => (s || "").replace(/\b\w/g, ch => ch.toUpperCase());

const fmtDuration = s => s == null ? "" : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

// Small metadata chip — keeps exercise rows readable instead of one long "·" string.
function MetaChip({ children, tone }) {
  const tones = { media: [C.primary, C.primaryGlow], default: [C.textSub, C.surfaceAlt] };
  const [color, background] = tones[tone] || tones.default;
  return <span style={{ fontSize: 10.5, fontWeight: 500, padding: "3px 8px", borderRadius: 6, background, color, whiteSpace: "nowrap" }}>{children}</span>;
}

// Any exercise video link → an embeddable URL. TikTok and Instagram refuse to be
// framed by their page URLs, so each needs its own embed URL; `vertical` marks
// portrait formats so they aren't letterboxed in a 16:9 frame. null = can't embed
// (e.g. a vm.tiktok.com short link) → the caller links out. Mirrors videoEmbed()
// in mypt-app.jsx.
const videoEmbed = (url) => {
  const raw = String(url || "").trim();
  if (!raw) return null;
  let u;
  try { u = new URL(raw); } catch { return null; }
  const host = u.hostname.replace(/^www\./, "");
  const path = u.pathname;
  if (/\.(mp4|webm|ogv|ogg|mov)$/i.test(path)) return { provider: "file", src: raw, file: true, vertical: false };
  if (host === "youtu.be") return { provider: "YouTube", src: `https://www.youtube.com/embed/${path.slice(1)}`, vertical: false };
  if (host.endsWith("youtube.com") || host.endsWith("youtube-nocookie.com")) {
    if (path.startsWith("/embed/")) return { provider: "YouTube", src: raw, vertical: false };
    if (path.startsWith("/shorts/")) return { provider: "YouTube", src: `https://www.youtube.com/embed/${path.split("/")[2]}`, vertical: true };
    const v = u.searchParams.get("v");
    return v ? { provider: "YouTube", src: `https://www.youtube.com/embed/${v}`, vertical: false } : null;
  }
  if (host.endsWith("tiktok.com")) {
    const m = /\/video\/(\d+)/.exec(path);
    return m ? { provider: "TikTok", src: `https://www.tiktok.com/embed/v2/${m[1]}`, vertical: true } : null;
  }
  if (host.endsWith("instagram.com")) {
    const m = /^\/(?:p|reel|reels|tv)\/([A-Za-z0-9_-]+)/.exec(path);
    return m ? { provider: "Instagram", src: `https://www.instagram.com/p/${m[1]}/embed`, vertical: true } : null;
  }
  if (host.endsWith("vimeo.com")) {
    const m = /\/(\d+)/.exec(path);
    return m ? { provider: "Vimeo", src: `https://player.vimeo.com/video/${m[1]}`, vertical: false } : null;
  }
  return null;
};
const ytEmbedUrl = url => (videoEmbed(url) || {}).src || null;   // kept for existing call sites

function mapFreeExercise(x) {
  const primary = (x.primaryMuscles || [])[0] || "";
  return {
    source: "free-exercise-db",
    source_id: x.id,
    name: x.name,
    category: FEDB_CATEGORY[x.category] || "Strength",
    body_area: x.category === "cardio" ? "Cardio" : (FEDB_BODY_AREA[primary] || "Full Body"),
    muscle: titleCase(primary) || null,
    equipment: x.equipment ? titleCase(x.equipment) : null,
    level: x.level || null,
    description: (x.instructions || []).map((step, i) => `${i + 1}. ${step}`).join("\n") || null,
    image_url: x.images && x.images[0] ? `${FEDB_BASE}/exercises/${x.images[0]}` : null,
    in_library: true,
  };
}

function ExerciseImportPanel({ existing, adminToken, onAuthExpired, onImported, onClose }) {
  const [all, setAll]         = useState(null); // raw dataset, fetched once
  const [loadErr, setLoadErr] = useState("");
  const [q, setQ]             = useState("");
  const [cat, setCat]         = useState("");
  const [muscle, setMuscle]   = useState("");
  const [equip, setEquip]     = useState("");
  const [level, setLevel]     = useState("");
  const [picked, setPicked]   = useState(() => new Set());
  const [busy, setBusy]       = useState(false);
  const [msg, setMsg]         = useState("");

  useEffect(() => {
    fetch(`${FEDB_BASE}/dist/exercises.json`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(data => setAll(Array.isArray(data) ? data : []))
      .catch(e => setLoadErr(`Couldn't load the open-source library (${e.message}).`));
  }, []);

  const importedIds   = new Set(existing.filter(e => e.source === "free-exercise-db").map(e => e.source_id));
  const existingNames = new Set(existing.map(e => (e.name || "").trim().toLowerCase()));
  const isIn = x => importedIds.has(x.id) || existingNames.has((x.name || "").trim().toLowerCase());
  const uniq = k => [...new Set((all || []).flatMap(x => [].concat(x[k] ?? [])))].filter(Boolean).sort();

  const shown = (all || []).filter(x =>
    (!q || x.name.toLowerCase().includes(q.toLowerCase())) &&
    (!cat || x.category === cat) &&
    (!muscle || (x.primaryMuscles || []).includes(muscle)) &&
    (!equip || x.equipment === equip) &&
    (!level || x.level === level));
  const selectable = shown.filter(x => !isIn(x));

  const toggle = id => setPicked(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAllShown = () => setPicked(p => { const n = new Set(p); selectable.forEach(x => n.add(x.id)); return n; });

  const runImport = async () => {
    if (busy) return;
    const chosen = (all || []).filter(x => picked.has(x.id) && !isIn(x)).map(mapFreeExercise);
    if (!chosen.length) { setMsg("Tick at least one exercise that isn't already in the library."); return; }
    setBusy(true); setMsg("");
    let inserted = 0, skipped = 0;
    try {
      for (let i = 0; i < chosen.length; i += 200) {
        const data = await adminRequest(adminToken, { action: "import_exercises", items: chosen.slice(i, i + 200) }, onAuthExpired);
        inserted += data.inserted || 0; skipped += data.skipped || 0;
        setMsg(`Importing… ${Math.min(i + 200, chosen.length)} / ${chosen.length}`);
      }
      setMsg(`Imported ${inserted} exercise${inserted === 1 ? "" : "s"}${skipped ? ` · ${skipped} skipped (already in the library)` : ""}.`);
      setPicked(new Set());
    } catch (e) {
      setMsg(`Import stopped: ${e.message}${inserted ? ` (${inserted} imported before the error)` : ""}`);
    }
    setBusy(false);
    onImported();
  };

  // Plain render helper (not a nested component) so selects don't remount.
  const sel = (value, set, options, placeholder) => (
    <select value={value} onChange={e => set(e.target.value)}
      style={{ padding: "9px 13px", background: C.surfaceAlt, color: C.text, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontFamily: "inherit", outline: "none" }}>
      <option value="">{placeholder}</option>
      {options.map(o => <option key={o} value={o}>{titleCase(o)}</option>)}
    </select>
  );

  return (
    <SectionCard title="Import open-source exercises">
      <div>
        <p style={{ fontSize: 13, color: C.textSub, lineHeight: 1.6, margin: "0 0 12px" }}>
          free-exercise-db — {all ? all.length : "…"} exercises with muscles, equipment, level, step-by-step instructions and images.
          Public domain (The Unlicense). Imported exercises are added to the platform library.
        </p>
        {loadErr && <div style={{ color: C.danger, fontSize: 13, marginBottom: 12 }}>{loadErr}</div>}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Input placeholder="Search by name…" value={q} onChange={setQ} style={{ flex: 1, minWidth: 180 }} />
          {sel(cat, setCat, uniq("category"), "All categories")}
          {sel(muscle, setMuscle, uniq("primaryMuscles"), "All muscles")}
          {sel(equip, setEquip, uniq("equipment"), "All equipment")}
          {sel(level, setLevel, uniq("level"), "All levels")}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", margin: "12px 0" }}>
          <span style={{ fontSize: 12, color: C.textMuted }}>
            {all ? `${shown.length} shown · ${selectable.length} not yet imported · ${picked.size} selected` : loadErr ? "" : "Loading library…"}
          </span>
          <div style={{ flex: 1 }} />
          <Btn sm outline onClick={selectAllShown}>Select all shown</Btn>
          <Btn sm outline onClick={() => setPicked(new Set())}>Clear</Btn>
          <Btn sm onClick={runImport}>{busy ? "Importing…" : `Import ${picked.size} selected`}</Btn>
          <Btn sm outline onClick={onClose}>Close</Btn>
        </div>
        {msg && <div style={{ fontSize: 13, color: C.textSub, marginBottom: 12 }}>{msg}</div>}
        <div style={{ maxHeight: 460, overflowY: "auto", border: `1px solid ${C.border}`, borderRadius: 8 }}>
          {shown.map(x => {
            const done = isIn(x);
            return (
              <label key={x.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 12px", borderBottom: `1px solid ${C.border}`, cursor: done ? "default" : "pointer", opacity: done ? 0.55 : 1 }}>
                <input type="checkbox" disabled={done || busy} checked={done || picked.has(x.id)} onChange={() => toggle(x.id)} />
                {x.images && x.images[0]
                  ? <img src={`${FEDB_BASE}/exercises/${x.images[0]}`} alt="" loading="lazy" style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 8, border: `1px solid ${C.border}`, flexShrink: 0, background: "#000" }} />
                  : <div style={{ width: 48, height: 48, borderRadius: 8, border: `1px solid ${C.border}`, background: C.surfaceAlt, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, color: C.textMuted }}>🏋️</div>}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{x.name}</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginTop: 5 }}>
                    {titleCase((x.primaryMuscles || [])[0]) && <MetaChip>{titleCase((x.primaryMuscles || [])[0])}</MetaChip>}
                    {x.equipment && <MetaChip>{titleCase(x.equipment)}</MetaChip>}
                    {x.level && <MetaChip>{titleCase(x.level)}</MetaChip>}
                    {x.category && <MetaChip>{titleCase(x.category)}</MetaChip>}
                  </div>
                </div>
                {done && <Badge label="In library" color={C.green} bg={C.greenLight} />}
              </label>
            );
          })}
          {all && shown.length === 0 && <div style={{ padding: 16, fontSize: 13, color: C.textMuted }}>No exercises match these filters.</div>}
        </div>
      </div>
    </SectionCard>
  );
}

// ── Find exercise videos (YouTube suggestions, admin-reviewed) ────────────────
// Calls suggest_videos in small batches until the requested number of searches,
// YouTube's daily quota, or the end of the list. Suggestions stay pending (hidden
// from PTs) until approved in the exercise's View.
function VideoFinderPanel({ exercises, adminToken, onAuthExpired, onChanged, onClose }) {
  const platform  = exercises.filter(e => e.pt_id == null);
  const adminOk   = platform.filter(e => e.video_url && !["auto", "platform"].includes(e.video_review_source)).length; // admin-approved or pasted by hand
  const autoOk    = platform.filter(e => e.video_url && e.video_review_source === "auto").length;
  const platformMod = platform.filter(e => e.video_url && e.video_review_source === "platform").length; // TikTok/IG, moderated by them
  const pending   = platform.filter(e => e.video_suggestion_status === "pending").length;
  const unscored  = pending; // every held item is re-judged against the current thresholds
  const rejected  = platform.filter(e => e.video_suggestion_status === "rejected").length;
  const noneFound = platform.filter(e => e.video_suggestion_status === "none_found").length;
  const toSearch  = platform.filter(e => !e.video_url && !e.video_suggestion_status).length;
  const [busy, setBusy]   = useState(false);
  const [msg, setMsg]     = useState("");
  const [rules, setRules] = useState(null);
  const stopRef = useRef(false);

  useEffect(() => {
    adminRequest(adminToken, { action: "video_settings" }, onAuthExpired).then(d => setRules(d.thresholds)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fmt = n => n >= 1e6 ? `${+(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${+(n / 1e3).toFixed(1)}k` : String(n);

  // maxSearches 0 = only score the held suggestions that have no counts yet.
  const run = async (maxSearches) => {
    if (busy) return;
    setBusy(true); stopRef.current = false; setMsg("");
    let auto = 0, held = 0, none = 0, pre = 0, end = "batch";
    try {
      if (maxSearches === 0) {
        const d = await adminRequest(adminToken, { action: "auto_review_pending" }, onAuthExpired);
        pre += d.preApproved || 0; end = d.quotaExceeded ? "quota" : "rescored";
      }
      while (maxSearches > 0 && auto + held + none < maxSearches) {
        if (stopRef.current) { end = "stopped"; break; }
        const d = await adminRequest(adminToken, { action: "suggest_videos", limit: Math.min(8, maxSearches - auto - held - none) }, onAuthExpired);
        pre += d.preApproved || 0; auto += d.autoApproved || 0; held += d.pending || 0; none += d.noneFound || 0;
        setMsg(`Searching… ${auto} auto-approved · ${held} held for review · ${none} no result`);
        if (d.quotaExceeded) { end = "quota"; break; }
        if (d.stoppedReason) { end = `error:${d.stoppedReason}`; break; }
        if (!d.autoApproved && !d.pending && !d.noneFound) { end = "done"; break; }
      }
    } catch (e) { end = `error:${e.message}`; }
    const earlier = pre ? ` · ${pre} earlier suggestion${pre === 1 ? "" : "s"} auto-approved` : "";
    const tally = `${auto} auto-approved · ${held} held for review · ${none} no result${earlier}`;
    setMsg(end === "quota" ? `YouTube's daily quota is used up (${tally}). Carry on tomorrow.`
      : end === "done" ? `Every platform exercise has been searched (${tally}).`
      : end === "rescored" ? `Scored the held suggestions — ${pre} auto-approved, the rest stay held for review.`
      : end === "stopped" ? `Stopped (${tally}).`
      : end.startsWith("error:") ? `Stopped — ${end.slice(6)} (${tally})`
      : `Batch finished (${tally}).`);
    setBusy(false);
    onChanged();
  };

  return (
    <SectionCard title="Find exercise videos (YouTube)">
      <div>
        <p style={{ fontSize: 13, color: C.textSub, lineHeight: 1.6, margin: "0 0 12px" }}>
          Searches YouTube for platform exercises that have no video. A result is <strong>auto-approved</strong> — shown to PTs straight away —
          only if it clears the bar{rules ? `: at least ${fmt(rules.minViews)} views, ${fmt(rules.minLikes)} likes, likes at ${+(rules.minLikeRate * 100).toFixed(2)}%+ of views, and ${fmtDuration(rules.minSeconds)}–${fmtDuration(rules.maxSeconds)} long` : ""}.
          Of the results that pass, the <strong>shortest</strong> wins — a demo should be brief but long enough to show the movement.
          Anything else is held for your review. Auto-approved videos stay marked until you check them, so you can spot-check when it suits you.
          About 100 searches a day; the exercises PTs use most go first.
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          <Badge label={`${adminOk} admin-approved`} color={C.green} bg={C.greenLight} />
          <Badge label={`${autoOk} auto-approved, not checked`} color={C.blue} bg={C.surfaceAlt} />
          <Badge label={`${pending} held for review`} color={C.amber} bg={C.amberLight} />
          {platformMod > 0 && <Badge label={`${platformMod} TikTok/Instagram pending approval (live)`} color={C.amber} bg={C.amberLight} />}
          <Badge label={`${toSearch} not searched yet`} color={C.textSub} bg={C.surfaceAlt} />
          {noneFound > 0 && <Badge label={`${noneFound} no result`} color={C.textSub} bg={C.surfaceAlt} />}
          {rejected > 0 && <Badge label={`${rejected} rejected`} color={C.textSub} bg={C.surfaceAlt} />}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <Btn sm onClick={() => run(8)}>{busy ? "Working…" : "Search next 8"}</Btn>
          <Btn sm outline onClick={() => run(100)}>Search up to 100</Btn>
          {unscored > 0 && <Btn sm outline onClick={() => run(0)}>Re-check held videos ({unscored})</Btn>}
          {busy && <Btn sm outline onClick={() => { stopRef.current = true; }}>Stop</Btn>}
          <Btn sm outline onClick={onClose}>Close</Btn>
        </div>
        {msg && <div style={{ fontSize: 13, color: C.textSub, marginTop: 12 }}>{msg}</div>}
      </div>
    </SectionCard>
  );
}

function ExerciseLibraryTab({ exercises, onRefresh, adminToken, onAuthExpired }) {
  const [search, setSearch]   = useState("");
  const [showImport, setShowImport] = useState(false);
  const [owner, setOwner]     = useState("platform"); // "platform" | "pt" | "all"
  const [viewEx, setViewEx]   = useState(null);       // exercise open in the read-only view
  const [showVideos, setShowVideos]   = useState(false);
  const [videoFilter, setVideoFilter] = useState("all");  // all | review | auto | admin | none
  const [reviewing, setReviewing]     = useState(false);

  const isAutoVideo = e => !!e.video_url && e.video_review_source === "auto";          // published, admin hasn't checked
  const isPlatformVideo = e => !!e.video_url && e.video_review_source === "platform";  // TikTok/IG — their moderation is the gate
  // Anything awaiting a decision: held suggestions (not live), unchecked auto-approvals,
  // and TikTok/IG pending approval (already live — reviewable, never blocking).
  const needsLook   = e => e.pt_id == null && (e.video_suggestion_status === "pending" || isAutoVideo(e) || e.video_suggestion_status === "pending_approval");
  const pendingCount = exercises.filter(e => e.pt_id == null && e.video_suggestion_status === "pending").length;
  const autoCount    = exercises.filter(e => e.pt_id == null && isAutoVideo(e)).length;
  const pendingOthers = viewEx ? exercises.filter(e => needsLook(e) && e.id !== viewEx.id) : [];
  const videoMatch = e => videoFilter === "all" ? true
    : videoFilter === "review" ? e.video_suggestion_status === "pending"
    : videoFilter === "auto"   ? isAutoVideo(e)
    : videoFilter === "admin"  ? !!e.video_url && !["auto", "platform"].includes(e.video_review_source)
    : videoFilter === "platform" ? isPlatformVideo(e)
    : !e.video_url;

  // Approve (→ the exercise's video, visible to PTs) or reject the pending suggestion.
  const reviewVideo = async (decision) => {
    if (!viewEx || reviewing) return;
    setReviewing(true);
    try {
      await adminRequest(adminToken, { action: "review_video", exerciseId: viewEx.id, decision }, onAuthExpired);
      // Mirror the server's change locally so the popup updates at once.
      setViewEx(v => v && ({
        ...v,
        // A pasted TikTok/IG has no suggestion URL — keep its video_url on approve.
        ...(decision === "approve" ? { video_url: v.video_suggestion_url || v.video_url, video_suggestion_status: "approved" } : {}),
        ...(decision === "reject" ? {
          video_url: v.video_url && (v.video_url === v.video_suggestion_url || v.video_suggestion_status === "pending_approval") ? null : v.video_url,
          video_suggestion_url: null, video_suggestion_title: null, video_suggestion_channel: null,
          video_views: null, video_likes: null, video_suggestion_status: "rejected",
        } : {}),
        video_review_source: "admin",
      }));
      onRefresh();
    } catch (e) { alert("Error: " + e.message); }
    setReviewing(false);
  };
  const [showModal, setShowModal] = useState(false);
  const [editEx, setEditEx]   = useState(null); // null = add, obj = edit
  const [form, setForm]       = useState({ name: "", body_area: "", muscle: "", description: "", teaching_points: "", video_url: "", in_library: true });
  const [saving, setSaving]   = useState(false);

  const openAdd = () => { setEditEx(null); setForm({ name: "", body_area: "", muscle: "", description: "", teaching_points: "", video_url: "", in_library: true }); setShowModal(true); };
  const openEdit = ex => { setEditEx(ex); setForm({ name: ex.name||"", body_area: ex.body_area||ex.bodyArea||"", muscle: ex.muscle||"", description: ex.description||"", teaching_points: ex.teaching_points||ex.teachingPoints||"", video_url: ex.video_url||ex.videoUrl||"", in_library: ex.in_library ?? ex.inLibrary ?? false }); setShowModal(true); };

  const handleSave = async () => {
    if (!form.name) { alert("Name is required."); return; }
    setSaving(true);
    // Via the service-role endpoint — anon writes to `exercises` are RLS-blocked.
    try {
      await adminRequest(adminToken, { action: "save_exercise", exercise: editEx ? { ...form, id: editEx.id } : form }, onAuthExpired);
    } catch (e) { alert("Error: " + e.message); setSaving(false); return; }
    setSaving(false);
    setShowModal(false);
    onRefresh();
  };

  const handleDelete = async ex => {
    const users = ex.ref_count || 0;
    const usage = users
      ? `\n\nIt's in ${users} PT${users === 1 ? "'s library" : "s' libraries"} — deleting removes it from ${users === 1 ? "that library" : "those libraries"}. Existing plans keep the exercise name.`
      : "";
    if (!window.confirm(`Delete exercise "${ex.name}"?${usage}`)) return;
    try {
      await adminRequest(adminToken, { action: "delete_exercise", exerciseId: ex.id }, onAuthExpired);
    } catch (e) { alert("Error: " + e.message); return; }
    onRefresh();
  };

  // Platform rows have no owning PT; everything else belongs to a PT (view-only here).
  const platformRows = exercises.filter(e => e.pt_id == null);
  const ptCount = exercises.length - platformRows.length;
  const filtered = exercises.filter(e => {
    const q = search.toLowerCase();
    const ownerOk = owner === "all" || (owner === "platform" ? e.pt_id == null : e.pt_id != null);
    return ownerOk && videoMatch(e) && (!q || e.name?.toLowerCase().includes(q) || (e.body_area || e.bodyArea || "").toLowerCase().includes(q) || (e.muscle || "").toLowerCase().includes(q) || (e.owner_name || "").toLowerCase().includes(q));
  });

  const Field = ({ label, field, multiline = false }) => (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.8, textTransform: "uppercase", color: C.textMuted, marginBottom: 6 }}>{label}</div>
      {multiline
        ? <textarea value={form[field]} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
            rows={3} style={{ width: "100%", padding: "9px 13px", background: C.surfaceAlt, color: C.text, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontFamily: "inherit", resize: "vertical", outline: "none" }} />
        : <Input value={form[field]} onChange={v => setForm(f => ({ ...f, [field]: v }))} style={{ width: "100%" }} />
      }
    </div>
  );

  return (
    <div>
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
        <Input placeholder="Search exercises…" value={search} onChange={setSearch} style={{ flex: 1, minWidth: 200, maxWidth: 360 }} />
        <select value={owner} onChange={e => setOwner(e.target.value)}
          style={{ padding: "9px 13px", background: C.surfaceAlt, color: C.text, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontFamily: "inherit", outline: "none" }}>
          <option value="platform">Platform library ({platformRows.length})</option>
          <option value="pt">PT exercises ({ptCount})</option>
          <option value="all">All exercises ({exercises.length})</option>
        </select>
        <Btn onClick={openAdd}>+ Add Exercise</Btn>
        <Btn outline onClick={() => setShowImport(s => !s)}>{showImport ? "Hide import" : "Import open-source exercises"}</Btn>
        <Btn outline onClick={() => setShowVideos(s => !s)}>{showVideos ? "Hide videos" : "Find videos"}</Btn>
        <select value={videoFilter} onChange={e => setVideoFilter(e.target.value)}
          style={{ padding: "9px 13px", background: C.surfaceAlt, color: C.text, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontFamily: "inherit", outline: "none" }}>
          <option value="all">All videos</option>
          <option value="review">Held for review ({pendingCount})</option>
          <option value="auto">Auto-approved, not checked ({autoCount})</option>
          <option value="platform">TikTok/Instagram pending approval (live)</option>
          <option value="admin">Admin-approved</option>
          <option value="none">No video</option>
        </select>
      </div>
      {showVideos && (
        <div style={{ marginBottom: 16 }}>
          <VideoFinderPanel exercises={exercises} adminToken={adminToken} onAuthExpired={onAuthExpired}
            onChanged={onRefresh} onClose={() => setShowVideos(false)} />
        </div>
      )}
      {showImport && (
        <div style={{ marginBottom: 16 }}>
          <ExerciseImportPanel existing={platformRows} adminToken={adminToken} onAuthExpired={onAuthExpired}
            onImported={onRefresh} onClose={() => setShowImport(false)} />
        </div>
      )}
      <SectionCard>
        <Table
          cols={["Name", "Owner", "Used by", "Body Area", "Muscle", "Category", "Source", "Has Video", "In Library", "Actions"]}
          rows={filtered.map(e => [
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {e.image_url && <img src={e.image_url} alt="" loading="lazy" style={{ width: 32, height: 32, objectFit: "cover", borderRadius: 6, border: `1px solid ${C.border}`, flexShrink: 0 }} />}
              <span onClick={() => setViewEx(e)} style={{ cursor: "pointer" }} title="View exercise">{e.name}</span>
            </div>,
            e.pt_id == null ? <span style={{ color: C.textMuted }}>Platform</span> : (e.owner_name || `PT #${e.pt_id}`),
            e.pt_id == null
              ? <span style={{ color: e.ref_count ? C.text : C.textMuted }}>{e.ref_count || 0} PT{e.ref_count === 1 ? "" : "s"}</span>
              : <span style={{ color: C.textMuted }}>—</span>,
            e.body_area || e.bodyArea || "—",
            e.muscle || "—",
            e.category || "—",
            e.source === "free-exercise-db"
              ? <Badge label="Open source" color={C.amber} bg={C.amberLight} />
              : <span style={{ color: C.textMuted }}>Manual</span>,
            (e.video_url || e.videoUrl)
              ? (e.video_review_source === "auto"
                  ? <Badge label="Auto ✓" color={C.blue} bg={C.surfaceAlt} />
                  : e.video_review_source === "platform"
                    ? <Badge label={`${e.video_metrics_source === "instagram" ? "Instagram" : "TikTok"} · pending, live`} color={C.amber} bg={C.amberLight} />
                    : <Badge label="Admin ✓" color={C.green} bg={C.greenLight} />)
              : e.video_suggestion_status === "pending" ? <Badge label="To review" color={C.amber} bg={C.amberLight} />
              : <span style={{ color: C.textMuted }}>No</span>,
            (e.in_library ?? e.inLibrary) ? <Badge label="Yes" color={C.primary} bg={C.primaryGlow} /> : <span style={{ color: C.textMuted }}>No</span>,
            <div style={{ display: "flex", gap: 6 }}>
              <Btn sm outline onClick={() => setViewEx(e)}>View</Btn>
              {e.pt_id == null && <Btn sm onClick={() => openEdit(e)}>Edit</Btn>}
              {e.pt_id == null && <Btn sm danger onClick={() => handleDelete(e)}>Delete</Btn>}
            </div>,
          ])}
        />
      </SectionCard>
      {viewEx && (
        <Modal title={viewEx.name} onClose={() => setViewEx(null)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {viewEx.image_url && (
              <img src={viewEx.image_url} alt={viewEx.name} style={{ width: "100%", maxHeight: 300, objectFit: "contain", borderRadius: 10, background: "#fff" }} />
            )}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <Badge label={viewEx.pt_id == null ? "Platform library" : `Owner: ${viewEx.owner_name || `PT #${viewEx.pt_id}`}`} color={C.primary} bg={C.primaryGlow} />
              {viewEx.pt_id == null && <Badge label={`Used by ${viewEx.ref_count || 0} PT${viewEx.ref_count === 1 ? "" : "s"}`} color={C.textSub} bg={C.surfaceAlt} />}
              {viewEx.platform_exercise_id && <Badge label={`Customised from platform #${viewEx.platform_exercise_id}`} color={C.textSub} bg={C.surfaceAlt} />}
              {[viewEx.body_area || viewEx.bodyArea, viewEx.muscle, viewEx.category, viewEx.equipment, viewEx.level].filter(Boolean).map(t => (
                <Badge key={t} label={t} color={C.textSub} bg={C.surfaceAlt} />
              ))}
              {viewEx.source === "free-exercise-db" && <Badge label="Open source · free-exercise-db" color={C.amber} bg={C.amberLight} />}
            </div>
            {[["Description", viewEx.description], ["Teaching points", viewEx.teaching_points || viewEx.teachingPoints], ["Adaptations", viewEx.adaptations]].map(([label, text]) => text ? (
              <div key={label}>
                <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.8, textTransform: "uppercase", color: C.textMuted, marginBottom: 6 }}>{label}</div>
                <div style={{ fontSize: 13, color: C.textSub, lineHeight: 1.7, whiteSpace: "pre-line" }}>{text}</div>
              </div>
            ) : null)}
            {Array.isArray(viewEx.alternatives) && viewEx.alternatives.length > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.8, textTransform: "uppercase", color: C.textMuted, marginBottom: 6 }}>Alternatives</div>
                <div style={{ fontSize: 13, color: C.textSub }}>{viewEx.alternatives.join(", ")}</div>
              </div>
            )}
            {(viewEx.video_url || viewEx.videoUrl) && (() => {
              const url = viewEx.video_url || viewEx.videoUrl;
              const em = videoEmbed(url);
              if (!em) {
                return <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: C.primary }}>Watch video ↗</a>;
              }
              if (em.file) {
                return <video src={em.src} controls playsInline style={{ width: "100%", maxHeight: 360, borderRadius: 10, background: "#000" }} />;
              }
              // Portrait formats (TikTok / Reels / Shorts) get a tall, narrow frame.
              const shape = em.vertical
                ? { maxWidth: 320, margin: "0 auto", paddingBottom: "min(177.78%, 560px)" }
                : { paddingBottom: "56.25%" };
              return (
                <div style={{ position: "relative", height: 0, borderRadius: 10, overflow: "hidden", background: "#000", ...shape }}>
                  <iframe src={em.src} title={viewEx.name} frameBorder="0"
                    allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen
                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} />
                </div>
              );
            })()}
            {viewEx.pt_id == null && viewEx.video_url && viewEx.video_suggestion_status === "pending_approval" && (
              /* TikTok/Instagram: LIVE on the site while pending approval — the platform's
                 moderation is the gate, since no view/like data exists to auto-approve.
                 In "Next to check", but nothing waits on it: it already plays. */
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <Badge label={`${viewEx.video_metrics_source === "instagram" ? "Instagram" : "TikTok"} — pending approval · live`} color={C.amber} bg={C.amberLight} />
                <span style={{ fontSize: 12, color: C.textMuted }}>Already playing for PTs; moderated by the platform.</span>
                <Btn sm onClick={() => reviewVideo("approve")}>{reviewing ? "Saving…" : "Approve"}</Btn>
                <Btn sm outline onClick={() => reviewVideo("reject")}>Remove video</Btn>
              </div>
            )}
            {viewEx.pt_id == null && viewEx.video_url && viewEx.video_suggestion_status === "approved" && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                {viewEx.video_review_source === "auto"
                  ? <Badge label="Auto-approved — not checked by admin" color={C.blue} bg={C.surfaceAlt} />
                  : <Badge label="Approved by admin" color={C.green} bg={C.greenLight} />}
                {viewEx.video_views != null && (
                  <span style={{ fontSize: 12, color: C.textMuted }}>
                    {Number(viewEx.video_views).toLocaleString()} views · {Number(viewEx.video_likes || 0).toLocaleString()} likes
                    {viewEx.video_seconds != null ? ` · ${fmtDuration(viewEx.video_seconds)} long` : ""}
                  </span>
                )}
                {viewEx.video_review_source === "auto" && (
                  <>
                    <Btn sm onClick={() => reviewVideo("confirm")}>{reviewing ? "Saving…" : "Looks good"}</Btn>
                    <Btn sm outline onClick={() => reviewVideo("reject")}>Remove video</Btn>
                  </>
                )}
              </div>
            )}
            {viewEx.pt_id == null && viewEx.video_suggestion_status === "pending" && viewEx.video_suggestion_url && (
              <div style={{ border: `1px solid ${C.amber}55`, borderRadius: 10, padding: 12, background: C.amberLight }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", color: C.amber, marginBottom: 8 }}>
                  Suggested video — PTs won't see it until you approve
                </div>
                {ytEmbedUrl(viewEx.video_suggestion_url) && (
                  <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, borderRadius: 8, overflow: "hidden", background: "#000", marginBottom: 8 }}>
                    <iframe src={ytEmbedUrl(viewEx.video_suggestion_url)} title={viewEx.video_suggestion_title || viewEx.name} frameBorder="0"
                      allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen
                      style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} />
                  </div>
                )}
                <div style={{ fontSize: 13, color: C.text }}>{viewEx.video_suggestion_title}</div>
                <div style={{ fontSize: 12, color: C.textMuted, margin: "2px 0 10px" }}>
                  {viewEx.video_suggestion_channel}
                  {viewEx.video_views != null
                    ? ` · ${Number(viewEx.video_views).toLocaleString()} views · ${Number(viewEx.video_likes || 0).toLocaleString()} likes${viewEx.video_seconds != null ? ` · ${fmtDuration(viewEx.video_seconds)} long` : ""} — outside the auto-approve bar`
                    : " · not scored yet"}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <Btn sm onClick={() => reviewVideo("approve")}>{reviewing ? "Saving…" : "Approve video"}</Btn>
                  <Btn sm outline onClick={() => reviewVideo("reject")}>Reject</Btn>
                </div>
              </div>
            )}
            {!viewEx.description && !viewEx.image_url && !(viewEx.video_url || viewEx.videoUrl) && (
              <div style={{ fontSize: 13, color: C.textMuted }}>No description or media for this exercise yet.</div>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
              {pendingOthers.length > 0 && <Btn outline onClick={() => setViewEx(pendingOthers[0])}>Next to check ({pendingOthers.length}) →</Btn>}
              {viewEx.pt_id == null && <Btn outline onClick={() => { const ex = viewEx; setViewEx(null); openEdit(ex); }}>Edit</Btn>}
              <Btn onClick={() => setViewEx(null)}>Close</Btn>
            </div>
          </div>
        </Modal>
      )}
      {showModal && (
        <Modal title={editEx ? "Edit Exercise" : "Add Exercise"} onClose={() => setShowModal(false)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Field label="Name *" field="name" />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Body Area" field="body_area" />
              <Field label="Muscle" field="muscle" />
            </div>
            <Field label="Description" field="description" multiline />
            <Field label="Teaching Points" field="teaching_points" multiline />
            <Field label="Video URL" field="video_url" />
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <input type="checkbox" id="inLib" checked={form.in_library} onChange={e => setForm(f => ({ ...f, in_library: e.target.checked }))} />
              <label htmlFor="inLib" style={{ fontSize: 13, color: C.textSub, cursor: "pointer" }}>Add to global library</label>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
              <Btn outline onClick={() => setShowModal(false)}>Cancel</Btn>
              <Btn onClick={handleSave}>{saving ? "Saving…" : editEx ? "Save Changes" : "Add Exercise"}</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── TAB 6 — SYSTEM ───────────────────────────────────────────────────────────
function SystemTab({ onRefresh }) {
  const [resetting, setResetting] = useState(false);

  const handleReset = async () => {
    if (!window.confirm("This will reset ALL localStorage data to seed data. Supabase data is unaffected. Continue?")) return;
    setResetting(true);
    await new Promise(r => setTimeout(r, 400));
    resetDB();
    setResetting(false);
    alert("Demo data reset to seed state.");
    onRefresh();
  };

  const settings = [
    { key: "Platform", value: "myPTOnline" },
    { key: "Version", value: "2.0 (Supabase integration)" },
    { key: "Trial Period", value: "60 days" },
    { key: "Supabase URL", value: "https://iydltqocplhfippfjhew.supabase.co" },
    { key: "Auth Strategy", value: "localStorage primary + Supabase background sync" },
    { key: "Admin Auth", value: "Hardcoded (bcrypt DB auth: coming soon)" },
  ];

  return (
    <div>
      <SectionCard title="Platform Settings">
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            {settings.map(s => (
              <tr key={s.key}>
                <td style={{ padding: "12px 0", fontSize: 13, fontWeight: 600, color: C.textSub, width: 220, borderBottom: `1px solid ${C.border}` }}>{s.key}</td>
                <td style={{ padding: "12px 0", fontSize: 13, color: C.text, borderBottom: `1px solid ${C.border}` }}>{s.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </SectionCard>
      <SectionCard title="Data Management">
        <p style={{ fontSize: 13, color: C.textSub, marginBottom: 16, lineHeight: 1.6 }}>
          Resetting demo data will restore localStorage to the original seed state. Useful for demos or testing. Supabase data is not affected by this action.
        </p>
        <Btn danger onClick={handleReset}>{resetting ? "Resetting…" : "Reset Demo Data"}</Btn>
      </SectionCard>
      <SectionCard title="Admin Account Management">
        <p style={{ fontSize: 13, color: C.textSub, lineHeight: 1.6 }}>
          Admin accounts are managed via the <code style={{ background: C.surfaceAlt, padding: "1px 6px", borderRadius: 4, fontSize: 12 }}>admins</code> table in Supabase.
          Password hashing (bcrypt) and multi-admin support will be wired here in a future update.
        </p>
      </SectionCard>
    </div>
  );
}

// ── MAIN ADMIN PANEL ─────────────────────────────────────────────────────────
// onAuthed / onSignedOut let the parent app sync its own `user` state (and so the
// header nav) the moment admin auth changes. Without them the panel authenticates
// in its own state only, and the nav keeps showing the logged-out buttons until a
// refresh re-runs the app's localStorage restore.
export default function AdminPanel({ onAuthed, onSignedOut } = {}) {
  const [adminToken, setAdminToken]       = useState(() => localStorage.getItem("mypt_admin_token") || "");
  const authed = !!adminToken;
  const [activeTab, setActiveTab]         = useState("overview");
  const [loading, setLoading]             = useState(false);
  const [pts, setPTs]                     = useState([]);
  const [clients, setClients]             = useState([]);
  const [sessions, setSessions]           = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [exercises, setExercises]         = useState([]);

  const stats = {
    totalPTs:      pts.length,
    totalClients:  clients.length,
    activeSubs:    subscriptions.filter(s => s.status === "active").length,
    trialUsers:    pts.length - subscriptions.filter(s => s.status !== "trial").length,
    expiredTrials: pts.filter(p => {
      const stored = (() => { try { return JSON.parse(localStorage.getItem("mypt_trial_start") || "{}"); } catch { return {}; } })();
      const start = stored[p.id] || p.trial_start || p.created_at;
      if (!start) return false;
      const elapsed = Math.floor((Date.now() - new Date(start)) / 86400000);
      return elapsed >= 60;
    }).length,
    totalSessions: sessions.length,
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // `clients` is RLS-locked against the anon admin client, so it's read via
      // the token-gated service-role endpoint (same as admin writes). pts is
      // publicly readable; sessions/subscriptions stay on anon.
      const clientsReq = fetch("/api/admin-action", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: adminToken, action: "list_clients" }),
      }).then(r => r.json()).then(j => j.clients || []).catch(() => []);

      // Platform exercise library — same RLS lock as clients, same endpoint.
      const exercisesReq = fetch("/api/admin-action", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: adminToken, action: "list_exercises" }),
      }).then(r => r.json()).then(j => j.exercises || []).catch(() => []);

      const [
        { data: ptsData },
        clientsData,
        { data: sessionsData },
        { data: subsData },
        exData,
      ] = await Promise.all([
        supabase.from("pts").select("*").order("id"),
        clientsReq,
        supabase.from("sessions").select("*").order("id"),
        supabase.from("subscriptions").select("*").order("id"),
        exercisesReq,
      ]);
      setPTs(ptsData || []);
      setClients(clientsData || []);
      setSessions(sessionsData || []);
      setSubscriptions(subsData || []);
      setExercises(exData || []);
    } catch (err) {
      console.warn("[AdminPanel] Supabase fetch failed, falling back to localStorage:", err.message);
      // Fallback to localStorage
      setPTs(getAll("pts"));
      setClients(getAll("clients"));
      setSessions(getAll("sessions"));
      setExercises(getAll("exercises"));
      setSubscriptions([]);
    }
    setLoading(false);
  }, [adminToken]);

  useEffect(() => {
    if (authed) loadData();
  }, [authed, loadData]);

  const signOut = () => { localStorage.removeItem("mypt_admin_token"); localStorage.removeItem("mypt_admin_authed"); setAdminToken(""); onSignedOut?.(); };

  if (!authed) return <AdminLogin onLogin={(token) => { setAdminToken(token); onAuthed?.(); }} />;

  const TABS = [
    { id: "overview",     label: "Overview" },
    { id: "pts",          label: "PT Management" },
    { id: "clients",      label: "Clients" },
    { id: "subscriptions",label: "Subscriptions" },
    { id: "exercises",    label: "Exercise Library" },
    { id: "system",       label: "System" },
  ];

  return (
    <div style={{
      minHeight: "100vh", background: C.bg, color: C.text,
      fontFamily: "'Space Grotesk', system-ui, sans-serif",
    }}>
      {/* Header */}
      <div style={{
        background: C.surface, borderBottom: `1px solid ${C.border}`,
        padding: "14px 28px", display: "flex", alignItems: "center",
        justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <span style={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.3px" }}>
            my<span style={{ color: C.primary }}>PT</span><span style={{ color: C.textMuted, fontWeight: 400 }}>Online</span>
          </span>
          <span style={{
            background: C.primaryGlow, color: C.primary, border: `1px solid ${C.primary}30`,
            borderRadius: 20, fontSize: 10, fontWeight: 700, padding: "2px 10px",
            letterSpacing: 0.5, textTransform: "uppercase",
          }}>Admin</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          {loading && <span style={{ fontSize: 12, color: C.textMuted }}>Loading…</span>}
          <Btn sm outline onClick={loadData}>Refresh</Btn>
          <Btn sm danger onClick={signOut}>Sign Out</Btn>
        </div>
      </div>

      <div style={{ maxWidth: 1300, margin: "0 auto", padding: "28px 24px" }}>
        {/* Tab Bar */}
        <div style={{
          display: "flex", gap: 2, background: C.surfaceAlt,
          borderRadius: 10, padding: 4, width: "fit-content",
          marginBottom: 24, overflowX: "auto",
        }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
              padding: "7px 16px", borderRadius: 7, fontSize: 13, fontWeight: 500,
              background: activeTab === t.id ? C.surface : "transparent",
              color: activeTab === t.id ? C.text : C.textSub,
              border: "none", cursor: "pointer", transition: "all 0.15s",
              fontFamily: "inherit", whiteSpace: "nowrap",
            }}>{t.label}</button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === "overview"      && <OverviewTab stats={stats} pts={pts} />}
        {activeTab === "pts"           && <PTManagementTab pts={pts} clients={clients} subscriptions={subscriptions} onRefresh={loadData} adminToken={adminToken} onAuthExpired={signOut} />}
        {activeTab === "clients"       && <ClientManagementTab clients={clients} pts={pts} />}
        {activeTab === "subscriptions" && <SubscriptionsTab subscriptions={subscriptions} pts={pts} onRefresh={loadData} />}
        {activeTab === "exercises"     && <ExerciseLibraryTab exercises={exercises} onRefresh={loadData} adminToken={adminToken} onAuthExpired={signOut} />}
        {activeTab === "system"        && <SystemTab onRefresh={loadData} />}
      </div>
    </div>
  );
}
